<template>
    <div>
        <pendo-alert
            v-if="error.value"
            type="error">
            {{ error.msg }}
        </pendo-alert>
        <div
            class="account-picker"
            data-cy="votes-modal-account-search">
            <div v-if="(initVisitorId !== '' || initAccountId !== '') && !ready">
                <pendo-loading-indicator size="small" />
            </div>
            <pendo-multiselect
                v-if="!error.value && !((initVisitorId !== '' || initAccountId !== '') && !ready)"
                :value="account"
                placeholder="Select an account..."
                value-key="id"
                label-key="accountName"
                :options="accountOptions"
                :searchable="true"
                :min-menu-width="720"
                :max-menu-width="720"
                :max-menu-height="400"
                :full-width="true"
                :internal-search="isSalesforceAccountMode"
                :allow-empty="true"
                :clear-on-select="true"
                sort-by="accountName"
                @select="onSelectAccount"
                @input-value-change="accountSearch"
                @highlighted-option-change="onHighlightedOptionChange($event, 'account')">
                <template #option="{ option }">
                    <div
                        v-pendo-tooltip="tooltipConfig(option.id, option.accountName)"
                        class="visitor-option">
                        <div class="visitor-option__label">
                            <pendo-icon
                                type="users"
                                size="16" />
                            <div
                                :id="`option-el-${option.id}`"
                                class="visitor-option__name">
                                {{ option.accountName }}
                            </div>
                        </div>
                    </div>
                </template>
                <template
                    v-if="account !== null"
                    #suffix>
                    <pendo-button
                        theme="app"
                        type="tertiary"
                        icon="x"
                        icon-size="14"
                        @click="clearAccount" />
                </template>
                <template
                    #noResult
                    v-if="loadingAccounts">
                    <pendo-loading-indicator size="small" />
                </template>
                <template #noData>
                    <div v-if="loadingAccounts">
                        <pendo-loading-indicator size="small" />
                    </div>
                    <div
                        v-else
                        class="visitor-picker__empty-state">
                        <div class="visitor-picker__empty-state__title">
                            {{ noAccountsText.title }}
                        </div>
                        <div class="visitor-picker__empty-state__subtitle">
                            {{ noAccountsText.subtitle }}
                        </div>
                    </div>
                </template>
                <template #aside>
                    <account-visitor-side-panel
                        :id="activeAccountId"
                        kind="account"
                        :name="highlightedOptionAccountName"
                        :preset-user-obj="findSalesforceAccountObject"
                        :is-salesforce-sourced="isActiveAccountSalesforceSourced" />
                </template>
            </pendo-multiselect>
        </div>
        <div
            v-if="!isSalesforceAccountMode"
            class="visitor-picker"
            data-cy="votes-modal-visitor-search">
            <div v-if="(initVisitorId !== '' || initAccountId !== '') && !ready">
                <pendo-loading-indicator size="small" />
            </div>
            <pendo-multiselect
                v-if="!error.value && !((initVisitorId !== '' || initAccountId !== '') && !ready)"
                :value="visitor"
                placeholder="Select a visitor..."
                value-key="id"
                label-key="visitorLabelKey"
                :options="visitorOptions"
                :searchable="true"
                :min-menu-width="720"
                :max-menu-width="720"
                :max-menu-height="400"
                :full-width="true"
                :internal-search="false"
                :allow-empty="true"
                sort-by="visitorLabelKey"
                @select="onSelectVisitor"
                @input-value-change="visitorSearch"
                @highlighted-option-change="onHighlightedOptionChange($event, 'visitor')">
                <template #option="{ option }">
                    <div class="visitor-option">
                        <div
                            v-pendo-tooltip="tooltipConfig(option.id, option.visitorLabelKey)"
                            class="visitor-option__label">
                            <pendo-icon
                                type="user"
                                size="16" />
                            <div
                                :id="`option-el-${option.id}`"
                                class="visitor-option__name">
                                {{ option.visitorLabelKey }}
                            </div>
                        </div>
                    </div>
                </template>
                <template
                    v-if="visitor !== null"
                    #suffix>
                    <pendo-button
                        theme="app"
                        type="tertiary"
                        icon="x"
                        icon-size="14"
                        @click="clearVisitor" />
                </template>
                <template
                    #noResult
                    v-if="loadingVisitors">
                    <pendo-loading-indicator size="small" />
                </template>
                <template #noData>
                    <div v-if="loadingVisitors">
                        <pendo-loading-indicator size="small" />
                    </div>
                    <div
                        v-else
                        class="visitor-picker__empty-state">
                        <div class="visitor-picker__empty-state__title">
                            {{ noVisitorsText.title }}
                        </div>
                        <div class="visitor-picker__empty-state__subtitle">
                            {{ noVisitorsText.subtitle }}
                        </div>
                    </div>
                </template>
                <template #aside>
                    <account-visitor-side-panel
                        :id="activeVisitorId"
                        kind="visitor"
                        :name="highlightedOptionVisitorName"
                        :is-salesforce-sourced="isActiveVisitorSalesforceSourced" />
                </template>
            </pendo-multiselect>
        </div>
    </div>
</template>

<script>
import {
    PendoMultiselect,
    PendoAlert,
    PendoLoadingIndicator,
    PendoIcon,
    PendoButton,
    PendoTooltip,
    PendoNotification,
} from '@pendo/components';
import get from 'lodash/get';
import { mapActions, mapState } from 'vuex';
import AccountVisitorSidePanel from '@/common/account-visitor-side-panel/account-visitor-side-panel.vue';
import { findAccounts, findVisitors } from '@/voc/api/account-visitor.api';
import { getPendoAccount } from '@/voc/api/voc-items.api';
import cloneDeep from 'lodash/cloneDeep';

export default {
    name: 'AccountVisitorPicker',
    components: {
        PendoMultiselect,
        PendoAlert,
        PendoLoadingIndicator,
        PendoIcon,
        PendoButton,
        AccountVisitorSidePanel,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        initAccountId: {
            type: String,
            required: false,
            default: '',
        },
        initVisitorId: {
            type: String,
            required: false,
            default: '',
        },
        isSalesforceAccountMode: {
            type: Boolean,
            default: false,
        },
        salesforceAccountObjects: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['visitor', 'account', 'isValid'],
    data () {
        return {
            accSearchText: '',
            accIdsList: [],
            forceAccountSearch: 0,
            visSearchText: '',
            visitor: null,
            visitorOptions: [],
            account: null,
            accountOptions: [],
            error: {
                value: false,
                msg: '',
            },
            loadingVisitors: false,
            loadingAccounts: false,
            debounceTimeout: 0,
            ready: false,
            activeAccountId: '',
            activeVisitorId: '',
            localSalesforceAccountObjects: [],
        };
    },
    computed: {
        ...mapState({
            settings: (state) => state.subscription.settings,
        }),
        accountDisplayName () {
            return get(this.settings, 'accountDisplayName.value', null);
        },
        visitorEmail () {
            return get(this.settings, 'visitorEmail.value', null);
        },
        findSalesforceAccountObject () {
            return this.localSalesforceAccountObjects.find((account) => account.id === this.activeAccountId);
        },
        highlightedOptionAccountName () {
            return this.accountOptions.find((account) => account.id === this.activeAccountId)?.accountName;
        },
        highlightedOptionVisitorName () {
            return this.visitorOptions.find((visitor) => visitor.id === this.activeVisitorId)?.visitorLabelKey;
        },
        emptyAccIdList () {
            return (
                !this.accIdsList ||
                this.accIdsList.length === 0 ||
                (this.accIdsList.length === 1 && this.accIdsList[0] === '')
            );
        },
        accountOptionsLength () {
            return this.accountOptions?.length;
        },
        visitorOptionsLength () {
            return this.visitorOptions?.length;
        },
        noVisitorsText () {
            if (this.visSearchText) {
                return {
                    title: 'No visitors match your search.',
                    subtitle: 'Double check your search or try searching a different visitor.',
                };
            }
            if (this.account) {
                return {
                    title: 'No visitors found for this account.',
                    subtitle: 'Try selecting another account.',
                };
            }

            return {
                title: 'No visitors found.',
                subtitle: '',
            };
        },
        noAccountsText () {
            if (this.accSearchText) {
                return {
                    title: 'No accounts match your search.',
                    subtitle: 'Double check your search or try searching a different account.',
                };
            }
            if (this.visitor) {
                return {
                    title: 'No accounts found for this visitor.',
                    subtitle: 'Try selecting another visitor.',
                };
            }

            return {
                title: 'No accounts found.',
                subtitle: '',
            };
        },
        isActiveAccountSalesforceSourced () {
            const activeAccount = this.accountOptions.find((account) => account.id === this.activeAccountId);

            return activeAccount?.metadata?.salesforce?.onlyFromSalesforce;
        },
        isActiveVisitorSalesforceSourced () {
            const activeVisitor = this.visitorOptions.find((visitor) => visitor.id === this.activeVisitorId);

            return activeVisitor?.metadata?.salesforce?.onlyFromSalesforce;
        },
    },
    watch: {
        accSearchText () {
            this.getAccountsFromBackend();
        },
        accIdsList () {
            this.getAccountsFromBackend();

            if (this.accountOptionsLength === 0) {
                this.activeAccountId = 'NO_RESULTS';
            }
        },
        visSearchText () {
            this.getVisitorsFromBackend();
        },
        account () {
            this.getVisitorsFromBackend();
        },
        loadingAccounts () {
            if (!this.loadingAccounts && this.accountOptionsLength === 0) {
                this.activeAccountId = 'NO_RESULTS';
            }
        },
        loadingVisitors () {
            if (!this.loadingVisitors && this.visitorOptionsLength === 0) {
                this.activeVisitorId = 'NO_RESULTS';
            }
        },
    },
    created () {
        if (this.isSalesforceAccountMode && Array.isArray(this.salesforceAccountObjects)) {
            this.salesforceAccountObjects.forEach((acc) => {
                acc.accountName = this.getSortableValue(acc, 'account');
            });
            this.localSalesforceAccountObjects = cloneDeep(this.salesforceAccountObjects);
            this.accountOptions = this.localSalesforceAccountObjects;
            this.ready = true;

            return;
        }

        this.accSearchText = this.initAccountId;
        this.visSearchText = this.initVisitorId;

        Promise.all([
            this.loadSetting({ setting: 'accountDisplayName', retrieveMetadata: false }),
            this.loadSetting({ setting: 'visitorEmail', retrieveMetadata: false }),
        ])
            .then(() => {
                this.getAccountsFromBackend();
                this.getVisitorsFromBackend();
                this.ready = true;
            })
            .catch((error) => error);
    },
    methods: {
        ...mapActions({
            loadSetting: 'subscription/loadSetting',
        }),
        onSelectAccount (option) {
            this.account = option;
            this.emitSelectedValues();
        },
        async onSelectVisitor (option) {
            this.visitor = option;
            if (
                this.account == null &&
                this.visitor?.metadata?.salesforce?.onlyFromSalesforce &&
                this.visitor?.metadata?.salesforce?.accountid
            ) {
                await this.getAccountsFromBackend(true);
            } else {
                this.accIdsList = this.visitor.accountIds;
                if (this.emptyAccIdList) {
                    this.account = null;
                    this.accountOptions = [];
                }
                this.emitSelectedValues();
            }
        },
        accountSearch (search = '') {
            if (!this.isSalesforceAccountMode) {
                if (this.debounceTimeout) clearTimeout(this.debounceTimeout);

                this.debounceTimeout = setTimeout(() => {
                    this.accSearchText = search;
                }, 500);
            }
        },
        visitorSearch (search = '') {
            if (this.debounceTimeout) clearTimeout(this.debounceTimeout);

            this.debounceTimeout = setTimeout(() => {
                this.visSearchText = search;
            }, 500);
        },
        clearAccount () {
            this.account = null;
            this.visitor = null;
            this.accSearchText = '';
            this.visSearchText = '';
            this.accIdsList = [];
            this.forceAccountSearch++;
            this.emitSelectedValues();
        },
        clearVisitor () {
            this.visitor = null;
            this.visSearchText = '';
            this.accIdsList = [];
            this.emitSelectedValues();
        },
        emitSelectedValues () {
            this.$emit('visitor', this.visitor);
            this.$emit('account', this.account);

            let isValid = false;
            if (this.account) {
                // If account is set, it is valid
                isValid = true;
            } else if (this.visitor) {
                if (!this.visitor.accountIds || this.visitor.accountIds.length < 1) {
                    // if visitor is set AND the visitor has no accounts, it is valid
                    isValid = true;
                } else if (
                    this.visitor.accountIds &&
                    this.visitor.accountIds.length > 0 &&
                    this.accountOptions?.length === 0
                ) {
                    // If visitor is set AND the visitor has accounts BUT the accountOptions are empty
                    // Then this is the edge case that the accounts are excluded, so we are ok to continue
                    isValid = true;
                }
            }
            this.$emit('isValid', isValid);
        },
        tooltipConfig (id, fullName) {
            const el = document.getElementById(`option-el-${id}`);
            if (el) {
                if (el.clientWidth < el.scrollWidth) {
                    return { content: fullName, arrow: true };
                }
            }

            return { content: null };
        },
        showBothNameAndId (id, name) {
            if (id && name) {
                return id.toString() !== name.toString();
            }

            return false;
        },
        onHighlightedOptionChange (option, kind) {
            if (option) {
                if (kind === 'account') {
                    this.activeAccountId = option?.id;
                } else {
                    this.activeVisitorId = option?.id;
                }
            }
        },
        async getAccountsFromBackend (forceSFAccountLookup = false) {
            try {
                if (this.visitor && !forceSFAccountLookup && this.emptyAccIdList) {
                    return;
                }

                this.accountOptions = [];
                this.loadingAccounts = true;

                let accList;
                if (forceSFAccountLookup && !!this.visitor?.metadata?.salesforce?.accountid) {
                    accList = await getPendoAccount(this.visitor.metadata.salesforce.accountid);
                } else {
                    accList = await findAccounts(this.accSearchText, this.accIdsList);
                }

                accList.forEach((acc) => {
                    if (acc.metadata?.salesforce?.onlyFromSalesforce) {
                        this.localSalesforceAccountObjects.push(acc);
                    }
                    acc.accountName = this.getSortableValue(acc, 'account');
                });

                this.accountOptions = accList;

                if (this.accountOptions?.length === 1 && this.visitor) {
                    this.onSelectAccount(this.accountOptions[0]);
                }

                this.loadingAccounts = false;
            } catch {
                this.loadingAccounts = false;
                PendoNotification({
                    type: 'error',
                    title: 'Error searching for accounts',
                    message: 'There was an error during the search. Please try again.',
                    duration: 3000,
                });
            }
        },
        async getVisitorsFromBackend () {
            try {
                this.loadingVisitors = true;
                this.visitorOptions = [];

                let accountSFDCId = '';
                let crmLookup = true;
                if (this.account) {
                    accountSFDCId = get(
                        this.account,
                        'metadata.salesforce.account__c',
                        get(this.account, 'metadata.salesforce.id', ''),
                    );

                    crmLookup = accountSFDCId !== '';
                }

                const visList = await findVisitors(this.visSearchText, this.account?.id, accountSFDCId, crmLookup);

                this.visitorOptions = visList.map((vis) => {
                    return {
                        ...vis,
                        visitorLabelKey: this.getSortableValue(vis, 'visitor'),
                    };
                });

                if (this.visitorOptions?.length === 1) {
                    if (!this.visitor || this.visitor.id !== this.visitorOptions[0].id) {
                        this.onSelectVisitor(this.visitorOptions[0]);
                    }
                }

                this.loadingVisitors = false;
            } catch {
                this.loadingVisitors = false;
                PendoNotification({
                    type: 'error',
                    title: 'Error searching for visitors',
                    message: 'There was an error during the search. Please try again.',
                    duration: 3000,
                });
            }
        },
        getSortableValue (obj, type) {
            let nameField = this.visitorEmail;
            if (type === 'account') {
                nameField = this.accountDisplayName;
            }

            if (this.isSalesforceSourced(obj)) {
                return this.getDisplayNameFromSalesforceObject(obj, nameField);
            }

            const displayName = get(obj, `metadata.${nameField}`, '');
            if (this.showBothNameAndId(obj.id, displayName)) {
                return `${displayName} | ${obj.id}`;
            }

            return obj.id;
        },
        isSalesforceSourced (object) {
            return this.isSalesforceAccountMode || !!object?.metadata?.salesforce?.onlyFromSalesforce;
        },
        getDisplayNameFromSalesforceObject (account, displayField) {
            const displayName = get(account, `metadata.${displayField}`, '');
            const sfdcName = get(account, 'metadata.salesforce.name', '');

            if (displayName !== '') {
                if (displayName !== sfdcName && sfdcName !== '') {
                    return `${displayName} | ${sfdcName}`;
                }

                if (displayName !== account.id) {
                    return `${displayName} | ${account.id}`;
                }

                return displayName;
            }

            if (sfdcName !== '') {
                return sfdcName;
            }

            return account.id;
        },
    },
};
</script>

<style scoped lang="less">
.account-picker,
.visitor-picker {
    margin-bottom: 10px;

    :deep(.pendo-multiselect__trigger .pendo-icon__x) {
        color: var(--palette-gray-70);
        margin-right: 10px;
    }
    :deep(.pendo_multiselect__element) {
        height: 100%;
    }
    &__empty-state {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 8px;
        padding: 0 16px;
        height: 100%;
    }
    &__empty-state__title {
        font-size: 16px;
        font-weight: 600;
        line-height: 135%;
        text-align: center;
    }
    &__empty-state__subtitle {
        font-size: 14px;
        line-height: 135%;
        text-align: center;
    }
}

.visitor-option {
    &__label {
        display: flex;
        align-items: center;
        height: 100%;
    }
    &__name {
        padding-left: 6px;
        text-overflow: ellipsis;
        overflow: hidden;
        line-height: initial;
    }
}
</style>
